import { AppShell, BrandLogo } from '@elva/ui';
import {
  LocationWorkspaceSwitcher,
  useAuthStore,
  useWorkspaceLabel,
} from '@elva/platform';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getNavKeyFromPath, NAV_PATHS, SIDEBAR_NAV } from '@/app/config/nav';
import { buildPageBreadcrumb } from '@/app/layouts/pageBreadcrumb';
import { PageTopBarProvider, usePageTopBarContext } from '@/app/layouts/pageTopBar';

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

function AppLayoutShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = getNavKeyFromPath(location.pathname);
  const workspaceLabel = useWorkspaceLabel();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { breadcrumb: breadcrumbOverride, actionsRef, actionsTick } = usePageTopBarContext();

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const displayName = user
    ? user.name.trim() || (user.email.split('@')[0] ?? 'Account')
    : 'Account';
  const roleLabel =
    user?.role && typeof user.role === 'object' && user.role.name
      ? user.role.name
      : 'AI Receptionist';

  const breadcrumb =
    breadcrumbOverride ?? buildPageBreadcrumb(location.pathname, workspaceLabel);
  const topBarActions = actionsTick >= 0 ? actionsRef.current : null;

  return (
    <AppShell
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapsedChange={setSidebarCollapsed}
      sidebar={{
        brand: {
          name: 'Elva',
          badge: 'AI Receptionist',
          logo: <BrandLogo src="/logo.svg" alt="Elva" />,
        },
        workspaceSlot: <LocationWorkspaceSwitcher collapsed={sidebarCollapsed} />,
        nav: SIDEBAR_NAV,
        active,
        onNav: (key) => {
          const path = NAV_PATHS[key];
          if (path) void navigate(path);
        },
        statusCard: {
          title: 'Elva is on duty',
          subtitle: 'since 7:00 am',
        },
        user: {
          name: displayName,
          role: roleLabel,
          initials: initialsFromName(displayName),
          onLogout: () => {
            void logout().catch(() => undefined);
          },
        },
      }}
      topbar={{
        breadcrumb,
        titleGradient: true,
        actions: topBarActions,
      }}
    >
      <Outlet />
    </AppShell>
  );
}

export function AppLayout() {
  return (
    <PageTopBarProvider>
      <AppLayoutShell />
    </PageTopBarProvider>
  );
}
